import { useState, useRef, useEffect } from 'react';

function AudioRecorder({ token }) {
  const [phase, setPhase] = useState('idle'); // idle, recording_problem, processing_problem, review_problem, recording_solution, processing_solution, completed
  const [recording, setRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [timer, setTimer] = useState(0);
  const [error, setError] = useState('');
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60).toString().padStart(2, '0');
    const s = (seconds % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const startRecording = async (forPhase) => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm;codecs=opus' });
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        stream.getTracks().forEach(t => t.stop());
        if (forPhase === 'problem') setPhase('processing_problem');
        else setPhase('processing_solution');
      };

      mediaRecorder.start(250);
      setRecording(true);
      setTimer(0);
      if (forPhase === 'problem') setPhase('recording_problem');
      else setPhase('recording_solution');

      timerRef.current = setInterval(() => setTimer(t => t + 1), 1000);
    } catch (err) {
      setError('No se pudo acceder al microfono. Verifica los permisos del navegador.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    }
  };

  // Enviar audio del problema
  useEffect(() => {
    if (phase === 'processing_problem' && audioBlob) {
      sendProblemAudio();
    }
    if (phase === 'processing_solution' && audioBlob) {
      sendSolutionAudio();
    }
  }, [phase, audioBlob]);

  const sendProblemAudio = async () => {
    setLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    try {
      const resp = await fetch('/api/cases/problem', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Error al procesar audio');
      setCaseData(data.case);
      setPhase('review_problem');
    } catch (err) {
      setError(err.message);
      setPhase('idle');
    } finally {
      setLoading(false);
      setAudioBlob(null);
    }
  };

  const sendSolutionAudio = async () => {
    if (!caseData) return;
    setLoading(true);
    setError('');
    const formData = new FormData();
    formData.append('audio', audioBlob, 'solution.webm');

    try {
      const resp = await fetch(`/api/cases/${caseData.id}/solution`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || 'Error al procesar audio');
      setCaseData(data.case);
      setPhase('completed');
    } catch (err) {
      setError(err.message);
      setPhase('review_problem');
    } finally {
      setLoading(false);
      setAudioBlob(null);
    }
  };

  const resetAll = () => {
    setPhase('idle');
    setCaseData(null);
    setAudioBlob(null);
    setError('');
    setTimer(0);
  };

  return (
    <div className="recorder-page">
      <h2 className="page-title">Registrar Caso de Soporte</h2>
      <p className="page-subtitle">Graba un audio describiendo el problema y la solucion aplicada</p>

      {error && <div className="error-box"><strong>Error:</strong> {error}</div>}

      {/* FASE IDLE */}
      {phase === 'idle' && (
        <div className="record-section">
          <div className="phase-indicator">
            <div className="phase-step active">1. Describir problema</div>
            <div className="phase-arrow">&rarr;</div>
            <div className="phase-step">2. IA analiza</div>
            <div className="phase-arrow">&rarr;</div>
            <div className="phase-step">3. Describir solucion</div>
          </div>
          <div className="record-prompt">
            <div className="mic-icon-large">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#4361ee" strokeWidth="1.5">
                <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/>
                <path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
              </svg>
            </div>
            <h3>Paso 1: Describe el problema</h3>
            <p>Presiona el boton y describe los sintomas del equipo que vas a reparar</p>
            <button className="btn btn-record" onClick={() => startRecording('problem')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="8"/></svg>
              Iniciar Grabacion
            </button>
          </div>
        </div>
      )}

      {/* GRABANDO PROBLEMA */}
      {phase === 'recording_problem' && (
        <div className="record-section">
          <div className="recording-active">
            <div className="recording-pulse"></div>
            <div className="recording-timer">{formatTime(timer)}</div>
            <p className="recording-label">Grabando... Describe el problema del equipo</p>
            <button className="btn btn-stop" onClick={stopRecording}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
              Detener Grabacion
            </button>
          </div>
        </div>
      )}

      {/* PROCESANDO PROBLEMA */}
      {(phase === 'processing_problem' || (phase === 'processing_solution')) && (
        <div className="record-section">
          <div className="processing-box">
            <div className="spinner"></div>
            <h3>Procesando audio con IA...</h3>
            <p>{phase === 'processing_problem' ? 'Transcribiendo y analizando el problema...' : 'Estandarizando la solucion aplicada...'}</p>
            <div className="processing-steps">
              <div className="proc-step active">Watson STT</div>
              <div className="proc-step">Granite AI</div>
              <div className="proc-step">Clasificacion</div>
            </div>
          </div>
        </div>
      )}

      {/* REVISION DEL PROBLEMA */}
      {phase === 'review_problem' && caseData && (
        <div className="record-section">
          <div className="analysis-result">
            <div className="analysis-header">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2"><path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              <h3>Problema analizado</h3>
            </div>

            <div className="analysis-grid">
              <div className="analysis-card category-card">
                <label>Categoria detectada</label>
                <div className="category-badge">{caseData.category}</div>
                <span className={`confidence-badge confidence-${caseData.confidence}`}>
                  Confianza: {caseData.confidence}
                </span>
              </div>

              <div className="analysis-card">
                <label>Lo que dijo el tecnico</label>
                <p className="transcript-text">"{caseData.problemTranscript}"</p>
              </div>

              <div className="analysis-card">
                <label>Resumen estandarizado</label>
                <p>{caseData.problemSummary}</p>
              </div>

              <div className="analysis-card">
                <label>Sintomas detectados</label>
                <div className="tags-list">
                  {(caseData.matchedSymptoms || []).map((s, i) => (
                    <span key={i} className="tag symptom-tag">{s}</span>
                  ))}
                </div>
              </div>

              <div className="analysis-card suggestion-card">
                <label>Solucion sugerida por IA</label>
                <p>{caseData.suggestedSolution}</p>
              </div>
            </div>

            <div className="review-actions">
              <div className="next-step-prompt">
                <h3>Paso 2: Describe la solucion aplicada</h3>
                <p>Graba un audio contando que hiciste para resolver el problema</p>
              </div>
              <div className="review-buttons">
                <button className="btn btn-record" onClick={() => startRecording('solution')}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="12" r="8"/></svg>
                  Grabar Solucion
                </button>
                <button className="btn btn-secondary" onClick={resetAll}>Cancelar caso</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* GRABANDO SOLUCION */}
      {phase === 'recording_solution' && (
        <div className="record-section">
          <div className="recording-active">
            <div className="recording-pulse solution-pulse"></div>
            <div className="recording-timer">{formatTime(timer)}</div>
            <p className="recording-label">Grabando... Describe lo que hiciste para resolver el problema</p>
            <button className="btn btn-stop" onClick={stopRecording}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="6" width="12" height="12" rx="2"/></svg>
              Detener Grabacion
            </button>
          </div>
        </div>
      )}

      {/* CASO COMPLETADO */}
      {phase === 'completed' && caseData && (
        <div className="record-section">
          <div className="completed-box">
            <div className="completed-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 11-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/>
              </svg>
            </div>
            <h3>Caso registrado exitosamente</h3>

            <div className="completed-summary">
              <div className="summary-row">
                <label>Categoria</label>
                <span className="category-badge">{caseData.category}</span>
              </div>
              <div className="summary-row">
                <label>Problema</label>
                <p>{caseData.problemSummary}</p>
              </div>
              <div className="summary-row">
                <label>Solucion aplicada</label>
                <p>{caseData.appliedSolution}</p>
              </div>
              {caseData.solutionSteps && caseData.solutionSteps.length > 0 && (
                <div className="summary-row">
                  <label>Pasos realizados</label>
                  <ol className="steps-list">
                    {caseData.solutionSteps.map((step, i) => <li key={i}>{step}</li>)}
                  </ol>
                </div>
              )}
              {caseData.partsUsed && caseData.partsUsed.length > 0 && (
                <div className="summary-row">
                  <label>Componentes utilizados</label>
                  <div className="tags-list">
                    {caseData.partsUsed.map((p, i) => <span key={i} className="tag part-tag">{p}</span>)}
                  </div>
                </div>
              )}
              {caseData.observations && (
                <div className="summary-row">
                  <label>Observaciones</label>
                  <p>{caseData.observations}</p>
                </div>
              )}
            </div>

            <button className="btn btn-primary" onClick={resetAll}>Registrar nuevo caso</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AudioRecorder;
